import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import NumberInput from '../../components/ui/NumberInput';

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

interface PlanPrice {
    billingCycle: 'monthly' | 'yearly';
    basePrice: number;
}

interface SubscriptionTier {
    id: string;
    name: string;
    rank: number;
    features: Record<string, boolean>;
    prices: PlanPrice[];
}

const FEATURE_LABELS: Record<string, string> = {
    dashboard: 'Dashboard & Analytics',
    attendance: 'Attendance Scanning',
    notifications: 'Automated Notifications',
    plans: 'Membership Plans Management',
    purchases: 'Purchase History & Billing',
    branches: 'Multi-branch Support',
    members: 'Member Management',
    team: 'Team & Staff Management',
    hrms: 'HRMS (Attendance, Leaves, Payslips)',
    staff_attendance: 'Staff Attendance (Selfie Check-in)',
    settings: 'Gym Configuration',
    class_scheduling: 'Class Scheduling & Booking',
    reports: 'Reports & Analytics',
    reach_ai: 'Reach AI (Follow-up Intelligence)',
    expense_tracking: 'Expense Tracking',
    gst_reports: 'GST Reports',
    'commerce.store_app': 'Member Store (app & web)',
    'commerce.fixed_slots': 'Fixed Weekly Slots',
};

export default function SubscriptionPlansTab({ token }: { token: string }) {
    const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);

    useEffect(() => {
        fetchTiers();
    }, []);

    const fetchTiers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/subscription-tiers`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (data.success) {
                setTiers(data.data);
            }
        } catch (error) {
            console.error('Error fetching tiers:', error);
            toast.error('Failed to load subscription plans');
        } finally {
            setLoading(false);
        }
    };

    const handleFeatureToggle = (tierId: string, featureKey: string) => {
        setTiers(prev => prev.map(t => {
            if (t.id === tierId) {
                return {
                    ...t,
                    features: {
                        ...t.features,
                        [featureKey]: !t.features[featureKey]
                    }
                };
            }
            return t;
        }));
    };

    const handlePriceChange = (tierId: string, cycle: 'monthly' | 'yearly', price: number) => {
        setTiers(prev => prev.map(t => {
            if (t.id === tierId) {
                const newPrices = [...t.prices];
                const priceIdx = newPrices.findIndex(p => p.billingCycle === cycle);
                if (priceIdx > -1) {
                    newPrices[priceIdx] = { ...newPrices[priceIdx], basePrice: price };
                } else {
                    newPrices.push({ billingCycle: cycle, basePrice: price });
                }
                return { ...t, prices: newPrices };
            }
            return t;
        }));
    };

    const saveTier = async (tier: SubscriptionTier) => {
        try {
            setSaving(tier.id);
            const response = await fetch(`${API_BASE_URL}/api/saas/subscription-tiers/${tier.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    features: tier.features,
                    prices: tier.prices,
                }),
            });

            const data = await response.json();
            if (data.success) {
                toast.success(`${tier.name} plan updated successfully`);
                // Update local state with fresh data
                setTiers(prev => prev.map(t => t.id === tier.id ? data.data : t));
            } else {
                throw new Error(data.error || 'Update failed');
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setSaving(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {tiers.map((tier) => (
                    <div key={tier.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                        {/* Plan Header */}
                        <div className={`p-6 text-white ${tier.name === 'Pro' ? 'bg-gradient-to-br from-indigo-600 to-purple-700' :
                            tier.name === 'Standard' ? 'bg-gradient-to-br from-emerald-500 to-teal-600' :
                                'bg-gradient-to-br from-amber-500 to-orange-600'
                            }`}>
                            <h3 className="text-xl font-bold">{tier.name}</h3>
                            <p className="text-white/80 text-sm">Priority Rank: {tier.rank}</p>
                        </div>

                        <div className="p-6 flex-1 flex flex-col space-y-6">
                            {/* Pricing Section */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Base Pricing</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Monthly (₹)</label>
                                        <NumberInput
                                            integer
                                            min={0}
                                            emptyValue={0}
                                            value={tier.prices.find(p => p.billingCycle === 'monthly')?.basePrice || 0}
                                            onChange={(v) => handlePriceChange(tier.id, 'monthly', v ?? 0)}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Yearly (₹)</label>
                                        <NumberInput
                                            integer
                                            min={0}
                                            emptyValue={0}
                                            value={tier.prices.find(p => p.billingCycle === 'yearly')?.basePrice || 0}
                                            onChange={(v) => handlePriceChange(tier.id, 'yearly', v ?? 0)}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Features Section */}
                            <div className="space-y-4 flex-1">
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Features</h4>
                                <div className="space-y-2">
                                    {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                                        <div key={key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                                            <span className="text-sm text-gray-700">{label}</span>
                                            <button
                                                onClick={() => handleFeatureToggle(tier.id, key)}
                                                className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${tier.features[key] ? 'bg-indigo-600' : 'bg-gray-200'
                                                    }`}
                                            >
                                                <span
                                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${tier.features[key] ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action */}
                            <button
                                onClick={() => saveTier(tier)}
                                disabled={saving === tier.id}
                                className={`w-full py-3 rounded-xl font-bold text-sm shadow-sm transition-all ${saving === tier.id
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-900 text-white hover:bg-black active:scale-[0.98]'
                                    }`}
                            >
                                {saving === tier.id ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
                                        <span>Saving Changes...</span>
                                    </div>
                                ) : 'Save Updates'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start space-x-3 text-blue-800">
                <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm">
                    <p className="font-bold mb-1">About Feature Flags</p>
                    <p>Disabling a feature here will immediately restrict access for ALL gyms currently on that plan. Use with caution as this may affect active business operations.</p>
                </div>
            </div>
        </div>
    );
}
